import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  CalendarDays, 
  Users, 
  UserCog, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  Settings,
  DollarSign,
  Wallet,
  BarChart3,
  Receipt,
  Building2,
  FileText
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = ({ children }) => {
  const { user, logout, hasRole, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [todayAppointmentsCount, setTodayAppointmentsCount] = useState(0);

  // Fetch today's appointments count
  useEffect(() => {
    const fetchTodayCount = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/dashboard/today-appointments`);
        setTodayAppointmentsCount(response.data.length);
      } catch (error) {
        console.error('Error fetching appointments count:', error);
      }
    };
    
    fetchTodayCount();
    // Refresh every 60 seconds
    const interval = setInterval(fetchTodayCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    toast.info('Sesión cerrada correctamente', {
      description: 'Has cerrado sesión exitosamente',
      duration: 3000,
    });
    navigate('/login');
  };

  const navItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: LayoutDashboard,
      roles: ['SUPERADMIN', 'ADMIN', 'RECEPTIONIST'],
    },
    {
      name: 'Agenda',
      path: '/agenda',
      icon: CalendarDays,
      roles: ['SUPERADMIN', 'ADMIN', 'RECEPTIONIST'],
    },
    {
      name: 'Pacientes',
      path: '/pacientes',
      icon: Users,
      roles: ['SUPERADMIN', 'ADMIN', 'RECEPTIONIST'],
    },
    {
      name: 'Ventas',
      path: '/ventas',
      icon: Receipt,
      roles: ['SUPERADMIN', 'ADMIN', 'RECEPTIONIST'],
    },
    {
      name: 'Cuentas por Cobrar',
      path: '/cuentas-por-cobrar',
      icon: FileText,
      roles: ['SUPERADMIN', 'ADMIN', 'RECEPTIONIST'],
    },
    {
      name: 'Comisiones',
      path: '/comisiones',
      icon: DollarSign,
      roles: ['SUPERADMIN', 'ADMIN'],
    },
    {
      name: 'Gastos y Finanzas',
      path: '/gastos',
      icon: Wallet,
      roles: ['SUPERADMIN', 'ADMIN'],
    },
    {
      name: 'Informes',
      path: '/informes',
      icon: BarChart3,
      roles: ['SUPERADMIN', 'ADMIN', 'RECEPTIONIST'],
    },
    {
      name: 'Sociedades',
      path: '/sociedades',
      icon: Building2,
      roles: ['SUPERADMIN', 'ADMIN'],
    },
    {
      name: 'Usuarios',
      path: '/usuarios',
      icon: UserCog,
      roles: ['SUPERADMIN'],
    },
    {
      name: 'Configuración',
      path: '/settings',
      icon: Settings,
      roles: ['SUPERADMIN', 'ADMIN'],
    },
  ];

  const filteredNavItems = navItems.filter((item) => hasRole(item.roles));

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleName = (role) => {
    const roles = {
      SUPERADMIN: 'Super Admin',
      ADMIN: 'Administrador',
      RECEPTIONIST: 'Recepcionista',
    };
    return roles[role] || role;
  };

  return (
    <div className="min-h-screen bg-[#e8f4f8] flex" data-testid="main-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        style={{
          background: 'linear-gradient(180deg, #0d3a6e 0%, #1a5192 100%)',
        }}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg p-1.5 shadow-md flex items-center justify-center">
                <img 
                  src="/images/logo.png" 
                  alt="Oftalmodiagnosis" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-heading font-bold text-lg text-white leading-tight">
                  Oftalmodiagnosis
                </span>
                <span className="text-xs text-white/70">
                  Sistema de Gestión
                </span>
              </div>
            </Link>
            <button 
              className="lg:hidden text-white hover:text-white/80"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group ${
                    isActive 
                      ? 'bg-white/20 text-white font-semibold' 
                      : 'text-white hover:bg-white/10'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-medium">{item.name}</span>
                  {/* Badge for Agenda showing today's appointments count */}
                  {item.name === 'Agenda' && todayAppointmentsCount > 0 && (
                    <Badge className="ml-auto bg-white/20 text-white text-xs px-2 py-0.5 hover:bg-white/20">
                      {todayAppointmentsCount}
                    </Badge>
                  )}
                  {isActive && item.name !== 'Agenda' && (
                    <ChevronRight className="w-4 h-4 ml-auto" strokeWidth={2} />
                  )}
                </Link>
              );
            })}
          </nav>
          
          {/* User section */}
          <div className="p-4 border-t border-white/20">
            <div className="flex items-center gap-3 px-4 py-3">
              <Avatar className="w-10 h-10 border-2 border-white/30">
                <AvatarFallback className="bg-brand-cyan text-white font-medium text-sm">
                  {user && getInitials(user.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">
                  {user?.full_name}
                </p>
                <p className="text-white/70 text-xs">
                  {user && getRoleName(user.role)}
                </p>
              </div>
            </div>
            
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full mt-2 flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-all duration-200"
              data-testid="sidebar-logout-button"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
              <span className="font-medium">Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </aside>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-[#e8f4f8] border-b border-slate-200/50 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-white rounded-lg transition-colors"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-button"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-xl font-semibold text-slate-800">
                  {filteredNavItems.find((item) => item.path === location.pathname)?.name || 'Dashboard'}
                </h1>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-brand-main text-white text-xs">
                    {user && getInitials(user.full_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline text-sm font-medium text-slate-700">
                  {user?.full_name}
                </span>
              </div>
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <main className="flex-1 p-6 md:p-8 lg:p-10">
          <div className="max-w-7xl mx-auto animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
